import type {
  ApplicationRecord, ApplicationTemplate, EventRecord, ProfileRecord
} from './types'

/**
 * The API surface, transport-agnostic.
 *
 * The package never calls fetch, wx.request or Taro.request — the platform
 * passes a `Transport` in. That is what keeps this file usable from React
 * Native later without a rewrite, and it makes the endpoint list one shared
 * definition instead of two that drift.
 */

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'DELETE'
  query?: Record<string, string | number | boolean | undefined>
  body?: unknown
}

export type Transport = <T>(path: string, options?: RequestOptions) => Promise<T>

export function createApi(request: Transport) {
  return {
    // ---- auth ----
    // `country_code` is the field name the backend reads. This used to send
    // `country`, which the server ignored — harmless only because the fallback
    // is +86 and every test number was Chinese. Both keys are accepted server
    // side so an older cached build of this package keeps working.
    /** Web: ask Aliyun to text a code. */
    sendSmsCode: (phone: string, country = '86') =>
      request<{ sent: boolean; expires_in: number; resend_in: number }>('/auth/sms/send', {
        method: 'POST', body: { phone, country_code: country }
      }),
    /** Web: exchange the code for the SAME app JWT the mini program gets. */
    verifySmsCode: (phone: string, code: string, country = '86', language: 'en' | 'zh' = 'zh') =>
      request<{ token: string; refresh_token: string; expires_in: number; profile: ProfileRecord }>(
        '/auth/sms/verify',
        { method: 'POST', body: { phone, code, country_code: country, language } }
      ),
    /** Ends the session SERVER-side. Forgetting the token locally is not enough. */
    logout: (refreshToken: string) =>
      request<void>('/auth/logout', { method: 'POST', body: { refresh_token: refreshToken } }),

    // ---- me ----
    getMe: () => request<ProfileRecord>('/me'),
    updateMe: (body: Partial<ProfileRecord>) =>
      request<ProfileRecord>('/me', { method: 'PATCH', body }),
    // The backend reads `phase` (MeController), NOT `onboarding_phase`. Sending
    // the wrong key made every submit end in `400 Invalid onboarding phase`
    // AFTER the answers had already saved — so the questionnaire was stored but
    // onboarding never completed. Same class of bug as country/country_code.
    // Both keys are sent so an older backend still understands it.
    updateOnboarding: (phase: string) =>
      request<ProfileRecord>('/me/onboarding', {
        method: 'PATCH', body: { phase, onboarding_phase: phase }
      }),
    /**
     * POST /me/avatar — base64, not multipart.
     * The mini program cannot use wx.uploadFile without a second whitelisted
     * domain, so the backend takes base64 on the normal request domain. The web
     * reuses it rather than growing a second upload path.
     */
    uploadAvatar: (contentType: string, base64: string) =>
      request<{ avatar_url: string; profile: ProfileRecord }>('/me/avatar', {
        method: 'POST', body: { content_type: contentType, data: base64 }
      }),

    // ---- application ----
    getApplicationTemplate: () => request<ApplicationTemplate>('/application-template'),
    getMyApplication: () => request<ApplicationRecord | null>('/me/application'),
    createMyApplication: (body: ApplicationRecord) =>
      request<ApplicationRecord>('/me/application', { method: 'POST', body }),
    updateMyApplication: (body: ApplicationRecord) =>
      request<ApplicationRecord>('/me/application', { method: 'PATCH', body }),

    // ---- events ----
    getEvents: (query?: { source?: string; type?: string }) =>
      request<EventRecord[]>('/events', { query }),
    getEvent: (id: string) => request<EventRecord>(`/events/${id}`),
    getEventAttendees: (id: string) =>
      request<{ total: number; attendees: Array<{ id: string; avatar_url: string | null }> }>(
        `/events/${id}/attendees`
      ),

    // ---- my seats ----
    //
    // THESE THREE WERE ALL WRONG, and each failed differently. The mini
    // program's services/matches.js is the reference — it is the version that
    // works against this backend.
    //
    //   list   : the endpoint returns a BARE ARRAY, not { matches }. Reading
    //            `.matches` gave undefined, so The Six always looked empty.
    //   join   : the route is /join/{eventId}, not a POST to the collection
    //            with an event_id body. That produced
    //            `HttpRequestMethodNotSupportedException: POST` -> 500.
    //   cancel : the backend reads `action`, not `status`.
    /** `type` is 'social' | 'vip'; omit for everything. */
    getMyEventMatches: (type?: 'social' | 'vip') =>
      request<Array<{ id?: string; event_id: string; status: string }>>(
        '/me/event-matches', { query: type ? { type } : undefined }
      ),
    joinEvent: (eventId: string) =>
      request<unknown>(`/me/event-matches/join/${eventId}`, { method: 'POST', body: {} }),
    cancelSeat: (matchId: string) =>
      request<unknown>(`/me/event-matches/${matchId}`, { method: 'PATCH', body: { action: 'cancel' } }),

    // ---- organize ----
    getOrganizeEventTemplate: () => request<ApplicationTemplate>('/organize-event-template'),
    listMyCommunityEventRequests: () => request<{ requests: unknown[] }>('/community-event-requests/me'),
    submitCommunityEventRequest: (body: unknown) =>
      request<unknown>('/community-event-requests', { method: 'POST', body }),

    // ---- restaurants ----
    getRestaurants: (query: { search?: string; page?: number; page_size?: number }) =>
      request<{ restaurants: unknown[]; total: number; page_count: number }>('/restaurants', { query })
  }
}

export type Api = ReturnType<typeof createApi>
